import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../axiosInstance";

const BASE = "/system-settings";

export const getCompanySettings = () =>
  axiosInstance.get(BASE).then((res) => res.data);

export const updateCompanySettings = (payload) =>
  axiosInstance.put(BASE, payload).then((res) => res.data);

export const getCompanySettingsAuditLogs = (params = {}) =>
  axiosInstance.get(`${BASE}/audit`, { params }).then((res) => res.data);

export const exportCompanySettingsAuditLogsCsv = () =>
  axiosInstance.get(`${BASE}/audit/export`, { responseType: "blob" });

export const useGetCompanySettings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(BASE);
      const res = response.data;
      if (res.success) {
        setData(res.data || null);
      } else {
        setError(res.message || "Failed to load company settings");
        setData(null);
      }
    } catch (err) {
      if (err.response) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            `Request failed (${err.response.status})`
        );
      } else if (err.request) {
        setError("Cannot reach server. Check that the backend is running.");
      } else {
        setError(err.message || "Failed to load company settings");
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
};

export const useUpdateCompanySettings = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(BASE, payload);
      return response.data;
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to update company settings";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useGetCompanySettingsAuditLogs = (params = {}, enabled = true) => {
  const { page = 1, limit = 50 } = params;
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: limit,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
    next_page: null,
    prev_page: null,
  });
  const [summary, setSummary] = useState({
    total_changes: 0,
    last_updated: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${BASE}/audit`, {
        params: { page, limit },
      });
      const res = response.data;
      if (res.success) {
        const payload = res.data || {};
        setData(payload.audit_logs || []);
        setPagination(
          payload.pagination || {
            current_page: 1,
            per_page: limit,
            total: 0,
            total_pages: 0,
            has_next: false,
            has_prev: false,
            next_page: null,
            prev_page: null,
          }
        );
        setSummary(
          payload.summary || {
            total_changes: 0,
            last_updated: null,
          }
        );
      } else {
        setError(res.message || "Failed to load audit logs");
        setData([]);
      }
    } catch (err) {
      if (err.response) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            `Request failed (${err.response.status})`
        );
      } else if (err.request) {
        setError("Cannot reach server. Check that the backend is running.");
      } else {
        setError(err.message || "Failed to load audit logs");
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    if (!enabled) return;
    fetchData();
  }, [fetchData, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    fetchData();
  }, [enabled, fetchData]);

  return { data, pagination, summary, loading, error, refetch };
};

export const useExportCompanySettingsAuditLogsCsv = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${BASE}/audit/export`, {
        responseType: "blob",
      });
      return response;
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to export audit logs";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};
